/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useLayoutEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { useOutletContext } from 'react-router-dom'
import type { MarketingOutletContextValue } from '@pages/Marketing/marketingOutletContext'
import './MarketingCampaignStyle.css'
import useMarketingCampaign from './useMarketingCampaign'
import CampaignOverview from '../Overview/CampaignOverview/CampaignOverview.component'
import { Button, DataGridComponent, SearchWithFilter } from '@components/common'
import { EmptyState } from '@components/ui'
import { ICampaignRequest } from '@dataTypes/marketing.type'
import { DropDownButtonComponent } from '@syncfusion/ej2-react-splitbuttons'
import { format } from 'date-fns'
import { ColumnModel } from '@syncfusion/ej2-react-grids'
import ButtonGroup from '@components/common/ButtonGroup/ButtonGroup.component'
import AddCampaignModal from '@components/layout/Modals/AddCampaign/AddCampaign.component'
import ViewCampaignModal from '@components/layout/Modals/ViewCampaignInfo/ViewCampaign.component'
import MarketingDeleteCampaignModal from '../MarketingDeleteCampaignModal.component'
import AdvancedSendModal from '../modal/AdvancedSend/AdvancedSend.component'
import CampaignCalendar from './Calendar/CampaignCalendar.component'
import { SwitchComponent } from '@syncfusion/ej2-react-buttons'
import { ClipMode } from '@constants/Common/GridComponent.contants'

const CAMPAIGN_TOOLBAR_ANCHOR_ID = 'mmc-campaign-toolbar-anchor'

const MarketingCampaign: React.FC = () => {
  const hooks = useMarketingCampaign()
  const { setShowCampaignCalendarNav, openAddCampaignModal } =
    useOutletContext<MarketingOutletContextValue>()
  const [toolbarMount, setToolbarMount] = useState<HTMLElement | null>(null)

  useLayoutEffect(() => {
    setToolbarMount(document.getElementById(CAMPAIGN_TOOLBAR_ANCHOR_ID))
    return () => setToolbarMount(null)
  }, [])

  useEffect(() => {
    return () => {
      setShowCampaignCalendarNav(false)
    }
  }, [setShowCampaignCalendarNav])

  const renderAdvancedSendButton = (
    campaign: ICampaignRequest,
  ): JSX.Element => {
    const isDisabled =
      campaign.campaign_status === 'Active' ||
      campaign.message_status === 'Draft' ||
      !!(
        campaign.start_date &&
        new Date(campaign.start_date) < new Date() &&
        campaign.end_date &&
        new Date(campaign.end_date) > new Date() &&
        campaign.campaign_status === 'Inactive'
      )

    let title = 'Send campaign in advance'
    if (campaign.campaign_status === 'Active')
      title = 'Campaign is already active'
    if (
      campaign.start_date &&
      new Date(campaign.start_date) < new Date() &&
      campaign.end_date &&
      new Date(campaign.end_date) > new Date() &&
      campaign.campaign_status === 'Inactive'
    )
      title =
        'This campaign has already ended. Advanced sending is not available.'

    return (
      <Button
        data-bs-toggle="tooltip"
        title={title}
        className="button-secondary"
        disabled={isDisabled}
        onClick={() => hooks.handleAdvancedSend(campaign)}
      >
        <i className="mgc_mail_send_fill" />
      </Button>
    )
  }

  const columns: ColumnModel[] = useMemo(
    () => [
      {
        headerText: 'Status',
        field: '',
        headerTextAlign: 'Center',
        textAlign: 'Center',
        template: (props: ICampaignRequest) => {
          const isDisabled = props.message_status === 'Draft'
          return (
            <span
              data-bs-toggle={isDisabled ? 'tooltip' : undefined}
              title={
                isDisabled
                  ? 'This trigger is still in draft mode. Complete the missing data to activate.'
                  : undefined
              }
              className="d-flex justify-content-center"
            >
              <SwitchComponent
                key={props.campaign_id}
                checked={props.campaign_status === 'Active'}
                change={(e) =>
                  hooks.handleToggleStatus(e.checked as boolean, props)
                }
                cssClass="e-small"
              />
            </span>
          )
        },
        width: 0.09,
      },
      {
        headerText: 'Campaign name',
        field: 'name',
        headerTextAlign: 'Left',
        textAlign: 'Left',
        width: 0.1,
        clipMode: ClipMode.EllipsisWithTooltip,
      },
      {
        headerText: 'Description',
        field: 'description',
        headerTextAlign: 'Left',
        textAlign: 'Left',
        width: 0.27,
        clipMode: ClipMode.EllipsisWithTooltip,
      },
      {
        headerText: 'Campaign start',
        field: '_start_date',
        headerTextAlign: 'Left',
        textAlign: 'Left',
        type: 'date',
        format: 'MM/dd/yyyy',
        headerTemplate: () => (
          <span className="e-headertext mc-header-with-info">
            Campaign start
            <i
              data-bs-toggle="tooltip"
              className="mgc_information_fill"
              title={`The date the campaign begins. Messages will be sent starting from this date if scheduled.`}
            />
          </span>
        ),
        width: 0.1,
      },
      {
        headerText: 'Campaign end',
        field: '_end_date',
        headerTextAlign: 'Left',
        textAlign: 'Left',
        headerTemplate: () => (
          <span className="e-headertext mc-header-with-info">
            Campaign end
            <i
              data-bs-toggle="tooltip"
              className="mgc_information_fill"
              title={`The date the campaign ends. No messages will be sent after this date.`}
            />
          </span>
        ),
        width: 0.1,
      },
      {
        headerText: 'Campaign type',
        field: 'type',
        headerTextAlign: 'Left',
        textAlign: 'Left',
        width: 0.1,
      },
      {
        headerText: 'Message status',
        field: 'message_status',
        headerTextAlign: 'Left',
        textAlign: 'Left',
        template: (props: ICampaignRequest) => {
          const className = `badge ${props.message_status.toLowerCase()}`
          return <span className={className}>{props.message_status}</span>
        },
        headerTemplate: () => (
          <span className="e-headertext mc-header-with-info">
            Message status
            <i
              data-bs-toggle="tooltip"
              className="mgc_information_fill"
              title={`The current delivery stage of the campaign message.
• Sent – Message has been successfully delivered.
• Scheduled – Message is queued and will be sent automatically at the scheduled time.
• Draft – Message is saved but not scheduled or sent yet.`}
            />
          </span>
        ),
        width: 0.11,
      },
      {
        headerText: 'Sent via',
        field: 'channel',
        headerTextAlign: 'Center',
        template: (props: ICampaignRequest) => {
          let icon = 'mgc_message_4_fill'
          if (props.channel === 'Email') icon = 'mgc_mail_fill'
          if (props.channel === 'Facebook') icon = 'mgc_facebook_fill'
          return (
            <div
              className="channel-icon"
              data-bs-toggle="tooltip"
              title={props.channel}
            >
              <i className={icon} />
            </div>
          )
        },
        textAlign: 'Center',
        width: 0.05,
      },
      {
        headerText: 'Actions',
        field: '',
        headerTextAlign: 'Center',
        template: (props: ICampaignRequest) => {
          return (
            <div className="actions-container">
              <Button
                data-bs-toggle="tooltip"
                title="View campaign"
                className="button-primary"
                onClick={() => hooks.handleViewCampaign(props)}
              >
                <i className="mgc_eye_2_fill" />
              </Button>
              {renderAdvancedSendButton(props)}
              <Button
                data-bs-toggle="tooltip"
                title={
                  props.campaign_status === 'Active'
                    ? 'Campaign is already active'
                    : 'Reactivate campaign'
                }
                className="button-secondary"
                disabled={
                  props.campaign_status === 'Active' ||
                  props.message_status === 'Draft'
                }
                onClick={() => hooks.handleReactivateCampaign(props)}
              >
                <i className="mgc_refresh_1_line" />
              </Button>
              <DropDownButtonComponent
                items={[
                  {
                    id: 'edit',
                    text: 'Edit',
                    iconCss: 'mgc_pencil_fill',
                  },
                  {
                    id: 'delete',
                    text: 'Delete',
                    iconCss: 'mgc_delete_2_fill',
                  },
                ]}
                cssClass="e-caret-hide mc-campaign-dd"
                iconCss="ti ti-dots-vertical"
                onClick={() => {
                  hooks.updateSelectedCampaign(props)
                }}
                select={hooks.handleDropdownAction}
              />
            </div>
          )
        },
        textAlign: 'Center',
        width: 0.11,
      },
    ],
    [hooks.filteredCampaigns],
  )

  const campaignToolbar = (
    <div className="mc-search-filter-container">
      <ButtonGroup
        buttons={[
          {
            label: '',
            icon: 'mgc_list_check_fill',
            value: 'list',
          },
          {
            label: '',
            icon: 'mgc_calendar_2_line',
            value: 'calendar',
          },
        ]}
        activeTab={hooks.activeTab}
        onButtonClick={(value) => {
          const next = value as 'list' | 'calendar'
          setShowCampaignCalendarNav(next === 'calendar')
          hooks.setActiveTab(next)
        }}
      />
      <SearchWithFilter
        searchIcon="mgc_search_2_line"
        filterIcon="mgc_settings_2_line"
        placeholder="Search"
        searchQuery={hooks.searchQuery}
        onChange={hooks.handleSearch}
        className="mcc-filter"
        filters={{
          messageStatus: ['Sent', 'Scheduled', 'Draft'],
          campaignStatus: ['Active', 'Inactive'],
          sentVia: ['Email', 'SMS'],
          campaignType: [
            'Promotional',
            'Recall & recare',
            'Reminder',
            'Celebration',
            'Patient education',
            'Follow-up',
            'Post-appt. review',
          ],
        }}
        onFilterChange={hooks.handleFilterChange}
        filterLabels={{
          messageStatus: 'Message status',
          campaignStatus: 'Status',
          sentVia: 'Sent via',
          campaignType: 'Campaign type',
        }}
      />
    </div>
  )

  const hasNoCampaigns = hooks.hasNoCampaigns
  const showEmptyState = hooks.filteredCampaigns.length === 0

  return (
    <>
      {toolbarMount ? createPortal(campaignToolbar, toolbarMount) : null}
      <div
        className={`marketing-campaign${
          showEmptyState ? ' marketing-campaign--empty' : ''
        }`}
      >
        {showEmptyState ? (
          <EmptyState
            className="marketing-campaign-empty"
            iconClass="mgc mgc_announcement_fill"
            title="No campaigns found"
            description={
              hasNoCampaigns
                ? "You don't have any campaigns on record yet."
                : 'No campaigns match your search.'
            }
            actionLabel={hasNoCampaigns ? 'Create campaign' : undefined}
            onAction={hasNoCampaigns ? openAddCampaignModal : undefined}
          />
        ) : hooks.activeTab === 'list' ? (
          <>
            <CampaignOverview
              hideTitle
              showFilter={true}
              showOverviewDropdown={true}
              setSelectedDropdownCampaignId={
                hooks.setSelectedDropdownCampaignId
              }
            />
            <DataGridComponent
              allowPaging={true}
              allowSorting={true}
              data={hooks.filteredCampaigns.map((c) => ({
                ...c,
                _start_date: c.start_date
                  ? format(new Date(c.start_date), 'MM/dd/yyyy')
                  : '',
                _end_date: c.end_date
                  ? format(new Date(c.end_date), 'MM/dd/yyyy')
                  : '',
              }))}
              columns={columns}
            />
          </>
        ) : (
          <CampaignCalendar campaigns={hooks.filteredCampaigns} />
        )}
        <MarketingDeleteCampaignModal
          show={hooks.showDeleteModal}
          campaignName={hooks.selectedCampaign?.name ?? ''}
          onCancel={hooks.onHideModal}
          onDelete={hooks.onConfirmDelete}
        />
        {hooks.showPreviewCampaign && hooks.selectedCampaign && (
          <ViewCampaignModal
            campaign={hooks.selectedCampaign}
            show={hooks.showPreviewCampaign}
            onHide={hooks.onHideModal}
          />
        )}
        {hooks.showEditCampaignModal && hooks.selectedCampaign && (
          <AddCampaignModal
            initialCampaign={hooks.selectedCampaign}
            show={hooks.showEditCampaignModal}
            onHide={hooks.onHideModal}
            method={hooks.isReactivateCampaign ? 'reactivate' : undefined}
          />
        )}
        {hooks.showAdvancedSendModal &&
          hooks.selectedCampaign &&
          hooks.selectedCampaign.start_date && (
            <AdvancedSendModal
              show={hooks.showAdvancedSendModal}
              onHide={hooks.onHideModal}
              startDate={hooks.selectedCampaign.start_date}
              selectedCampaign={hooks.selectedCampaign}
            />
          )}
      </div>
    </>
  )
}

export default MarketingCampaign
